import { defineComponent } from '~/utils/alpine_types'

// Use discriminated unions to support both single and multiple selects as either numbers or strings
// Values are strings by default, but outer Alpine.js components may be using `x-model.number`
type SingleStateString = { mode: 'single'; type: 'string'; value: string | null }
type SingleStateNumber = { mode: 'single'; type: 'number'; value: number | null }
type MultiStateString = { mode: 'multiple'; type: 'string'; value: string[] }
type MultiStateNumber = { mode: 'multiple'; type: 'number'; value: number[] }
type SelectAttributes = { disabled: boolean; invalid: boolean; required: boolean }
type SelectState = (SingleStateString | SingleStateNumber | MultiStateString | MultiStateNumber) & SelectAttributes

type SelectOption = {
  value: number | string
  label: string
  disabled: boolean
}

type Optgroup = {
  label: string
  disabled: boolean
  optionValues: (number | string)[]
}

type OptionButtonElement = { option: SelectOption; $el: HTMLButtonElement; $event: Event | undefined }

// @ts-expect-error: RegExp.escape is newer than the target ES version
// eslint-disable-next-line
const escapedString = (str: string): string => (typeof RegExp.escape === 'function' ? RegExp.escape(str) : str)

// This is used to determine whether anchor positioning will be used or if JS-based styles are needed.
// The polyfill is loaded based on support for 'anchor-name', but the CSS requires the newer `position-area: bottom`.
const supportsAnchorPositioning = CSS.supports('position-area', 'bottom')

export const customSelect = defineComponent(() => ({
  selectElement: undefined as unknown as HTMLSelectElement,
  popoverToggleElement: undefined as unknown as HTMLDivElement,
  popoverElement: undefined as unknown as HTMLElement,
  options: [] as SelectOption[],
  optgroups: [] as Optgroup[],
  selectState: {
    mode: 'single',
    type: 'string',
    value: null,
    disabled: false,
    invalid: false,
    required: false,
  } as SelectState,
  query: '',
  prompt: '',
  skipEvents: false,
  popoverBelow: true,
  suppressErrorMessage: true,
  optionObserver: undefined as unknown as MutationObserver,
  eventListeners: [] as {
    eventTarget: EventTarget
    type: string
    listener: EventListener
    options?: AddEventListenerOptions
  }[],

  get availableOptions(): SelectOption[] {
    let available = this.options

    if (this.query.length) {
      const searchTerms = this.query
        .toLowerCase()
        .split(/\s+/)
        .map((str) => escapedString(str))
      for (const q of searchTerms) {
        available = available.filter(({ label }) => label.toLowerCase().match(q))
      }
    }

    if (this.selectState.mode === 'multiple' && this.selectState.value.length) {
      if (this.selectState.type === 'number') {
        const selectedValues = this.selectState.value
        available = available.filter(({ value }) => !selectedValues.includes(value as number))
      } else {
        const selectedValues = this.selectState.value
        available = available.filter(({ value }) => !selectedValues.includes(value as string))
      }
    }

    return available
  },

  get emptySelection(): boolean {
    return this.selectState.mode === 'single' ? !this.selectState.value : !this.selectState.value.length
  },

  get selectedOptions(): SelectOption[] {
    switch (this.selectState.mode) {
      case 'single': {
        const selectedOption = this.options.find(({ value }) => value === this.selectState.value)
        return selectedOption ? [selectedOption] : []
      }
      case 'multiple': {
        // Use the order of selected values to preserve the ordering of user selections
        return this.selectState.value.map((id) => this.options.find((option) => option.value === id)!)
      }
      default: {
        return []
      }
    }
  },

  get requiredFieldInvalid(): boolean {
    return !this.suppressErrorMessage && this.selectState.required && this.emptySelection
  },

  init(): void {
    this.selectElement = this.$root.querySelector('select') as HTMLSelectElement
    this.popoverToggleElement = this.$refs.popoverToggle as HTMLDivElement
    this.popoverElement = this.$root.querySelector('[popover]') as HTMLElement

    if (!this.selectElement) throw new Error('Select tag not found')
    if (!this.popoverToggleElement) throw new Error('Popover toggle element not found')
    if (!this.popoverElement) throw new Error('Popover element not found')

    if (this.selectElement.hasAttribute('x-model.number')) {
      this.selectState.type = 'number'
    }

    if (this.selectElement.multiple) {
      this.selectState.mode = 'multiple'
      this.selectState.value = []
    }

    this.optionObserver = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        const element = mutation.target as HTMLOptionElement
        const oldValue =
          mutation.type === 'attributes' && mutation.attributeName === 'value' && mutation.oldValue
            ? this.convertOptionValue(mutation.oldValue)
            : this.convertOptionValue(element.value)
        const newValue = this.convertOptionValue(element.value)
        const selectOption = this.options.find(({ value }) => value === oldValue)
        if (!selectOption) return

        if (mutation.type === 'attributes') {
          switch (mutation.attributeName) {
            case 'disabled':
              selectOption.disabled = !!element.disabled
              break
            case 'value':
              selectOption.value = newValue
              break
          }
        }

        // The innerText of an <option> is a Text node, which triggers a childList mutation
        if (mutation.type === 'childList') {
          selectOption.label = element.label
        }
      }
    })

    // Populate `options` and `optgroups` from the hidden select element's state
    for (const option of this.selectElement.querySelectorAll('option')) {
      this.addOption(option)
    }

    for (const optgroup of this.selectElement.querySelectorAll('optgroup')) {
      const { label, disabled } = optgroup
      const optionValues = [...optgroup.querySelectorAll('option')].map(({ value }) => this.convertOptionValue(value))
      this.optgroups.push({ label, disabled, optionValues })
    }

    this.$watch('selectState.value', () => this.dispatchEvents())

    // The hidden select gets cleared on reset, but it doesn't get reflected in `selectState.value` automatically.
    // Native form fields don't dispatch events on form reset, so dispatching events should be disabled temporarily.
    const resetListener = () => {
      this.skipEvents = true
      this.selectState.value = this.selectState.mode === 'single' ? null : []
      setTimeout(() => (this.skipEvents = false), 0)
    }

    this.selectState.disabled = this.selectElement.disabled

    // Error messages should only be shown after the user interacts with the element or submits the form
    this.popoverElement.addEventListener('focusout', () => (this.suppressErrorMessage = false))
    const submitListener = (e: Event) => {
      const form = e.target as HTMLFormElement
      if (!this.selectState.required || !this.emptySelection) return

      // Avoid validation if novalidate is set on the submit button or the form
      const submitButton = form.querySelector('input[type="submit"]') || form.querySelector('button[type="submit"]')
      if (submitButton?.hasAttribute('formnovalidate') || form.noValidate) return

      e.preventDefault()
      e.stopPropagation()
      this.suppressErrorMessage = false

      const focusElement = form.querySelector<HTMLElement>(':invalid') || this.popoverToggleElement
      focusElement.focus()
    }

    const formElement = this.selectElement.form
    if (formElement) {
      // Event listeners on outer elements should be tracked so they can be removed once the component is destroyed
      this.setEventListener(formElement, 'reset', resetListener)
      this.setEventListener(formElement, 'submit', submitListener)
    }

    // Reset search query when opening the select
    this.popoverToggleElement.addEventListener('click', () => (this.query = ''))

    // Update JS-based styles (fallbacks and styles that depend on popover direction)
    const domListener = () => this.updatePopoverStyles()
    this.setEventListener(window, 'resize', domListener)
    this.setEventListener(window, 'scroll', domListener, { capture: true })

    // Detect popover opening/closing
    const popoverObserver = new IntersectionObserver(domListener)
    popoverObserver.observe(this.popoverElement)

    // Observe for changes on the original select to handle dynamically updated options and attributes
    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            node instanceof HTMLOptionElement && this.addOption(node)
          }
          for (const node of mutation.removedNodes) {
            node instanceof HTMLOptionElement && this.removeOption(node)
          }
        }

        if (mutation.type === 'attributes') {
          const select = mutation.target as HTMLSelectElement
          switch (mutation.attributeName) {
            case 'disabled':
              this.selectState.disabled = select.disabled
              break
            case 'data-required':
              // The ViewComponent converts static and dynamic required values to :data-required
              this.selectState.required = !!select.dataset.required
              break
            case 'aria-invalid':
              // aria-invalid can be used on the <select> to signal an invalid state
              this.selectState.invalid = !!select.ariaInvalid && select.ariaInvalid !== 'false'
              break
          }
        }
      }
    })

    observer.observe(this.selectElement, { childList: true, attributes: true })
  },

  // Clear event listeners attached to outer elements when destroying the component
  destroy(): void {
    for (const { eventTarget, type, listener, options } of this.eventListeners) {
      eventTarget.removeEventListener(type, listener, options)
    }
    this.eventListeners = []
  },

  // Sets an eventListener and adds it to the tracked listeners which are cleared when the component gets destroyed
  setEventListener(
    eventTarget: EventTarget,
    type: string,
    listener: EventListener,
    options?: AddEventListenerOptions,
  ): void {
    eventTarget.addEventListener(type, listener, options)
    this.eventListeners.push({ eventTarget, type, listener, options })
  },

  addSelection(value: number | string, event?: Event): void {
    if (typeof value !== this.selectState.type) throw new Error('Value type does not match selectState')

    // @ts-expect-error: `push` isn't satisified with the `typeof` check above, but it should be enough
    this.selectState.mode === 'single' ? (this.selectState.value = value) : this.selectState.value.push(value)

    if (this.selectState.mode === 'single') {
      this.popoverElement.hidePopover()
      return
    }

    if (event instanceof KeyboardEvent) {
      const nextOption = this.nextOption(this.$el as HTMLButtonElement)
      nextOption ? nextOption.focus() : this.focusSearchField()
    }
  },

  removeSelection(value: number | string): void {
    this.suppressErrorMessage = false
    if (this.selectState.mode === 'single') return

    const index =
      this.selectState.type === 'number'
        ? this.selectState.value.indexOf(+value)
        : this.selectState.value.indexOf(String(value))

    index >= 0 && this.selectState.value.splice(index, 1)
  },

  clearSelection(): void {
    this.selectState.value = this.selectState.mode === 'single' ? null : []
    this.suppressErrorMessage = false
  },

  // Changing the value via Alpine doesn't trigger events on the select, they need to be triggered manually
  dispatchEvents(): void {
    if (this.skipEvents) return

    this.selectElement.dispatchEvent(new Event('input', { bubbles: true }))
    this.selectElement.dispatchEvent(new Event('change', { bubbles: true }))
  },

  addOption(option: HTMLOptionElement): void {
    this.optionObserver.observe(option, { attributes: true, attributeOldValue: true, childList: true })

    const { label, selected, disabled } = option
    const value = !option.value.length
      ? null
      : this.selectState.type === 'number'
        ? +option.value
        : String(option.value)

    if (!value) {
      label.length && (this.prompt = label)
      return
    }

    // Add the new option in the same relative position as it is in the original select (empty values aren't included)
    let previousElementIndex = -1
    const prev = this.previousHtmlOption(option)

    if (prev && prev instanceof HTMLOptionElement && prev.value !== '') {
      const previousValue = this.convertOptionValue(prev.value)

      for (let i = this.options.length - 1; i >= 0; i--) {
        if (this.options[i]!.value === previousValue) {
          previousElementIndex = i
          break
        }
      }
    }

    this.options.splice(previousElementIndex + 1, 0, { value, label, disabled })
    selected && this.addSelection(value)
  },

  removeOption(option: HTMLOptionElement): void {
    this.removeSelection(option.value)

    if (option.value === '') {
      this.prompt = ''
      return
    }

    this.options.forEach((existingOption, i) => {
      const value = this.convertOptionValue(option.value)
      if (existingOption.value === value) {
        this.options.splice(i, 1)
        return
      }
    })
  },

  focusFirstOption(): void {
    const firstSelectableOption = this.popoverElement.querySelector<HTMLButtonElement>('button:not(:disabled)')
    firstSelectableOption?.focus()
  },

  focusSearchField(): void {
    this.popoverElement.querySelector('input')?.focus()
  },

  focusNextOption(currentElement: HTMLButtonElement): void {
    const nextOption = this.nextOption(currentElement)
    if (nextOption?.disabled) return this.focusNextOption(nextOption)

    nextOption?.focus()
  },

  focusPreviousOption(currentElement: HTMLButtonElement): void {
    const previousOption = this.previousOption(currentElement)
    if (previousOption?.disabled) return this.focusPreviousOption(previousOption)

    previousOption?.focus()
  },

  // Options may be wrapped in fieldsets when using optgroups, othrewise they're just in a list
  nextOption(currentElement: HTMLButtonElement): HTMLButtonElement | null | undefined {
    const nextElement = currentElement.nextElementSibling
    if (nextElement instanceof HTMLButtonElement) return nextElement
    if (!this.optgroups.length && !nextElement) return null

    return currentElement.closest('fieldset')?.nextElementSibling?.querySelector('button')
  },

  // Get previous custom select's option button element
  previousOption(currentElement: HTMLButtonElement): HTMLButtonElement | null | undefined {
    const previousElement = currentElement.previousElementSibling
    if (previousElement instanceof HTMLButtonElement) return previousElement
    if (!this.optgroups.length && !previousElement) return null

    const previousOptgroup = currentElement.closest('fieldset')?.previousElementSibling
    return previousOptgroup?.querySelector('[x-bind="OptionButton"]:last-of-type')
  },

  // Get previous real <option> element
  previousHtmlOption(currentOption: HTMLOptionElement): HTMLOptionElement | null | undefined {
    const previousOption = currentOption.previousElementSibling
    return previousOption instanceof HTMLOptionElement
      ? previousOption
      : currentOption.closest('optgroup')?.previousElementSibling?.querySelector('option:last-of-type')
  },

  togglePopover(): void {
    const origState = this.popoverElement.matches(':popover-open')

    // Older iOS versions (at least iOS 18) open the popover correctly otherwise, but not when it's in a modal
    // This should catch situations where the popover doesn't get toggled correctly
    setTimeout(() => {
      if (origState === this.popoverElement.matches(':popover-open')) {
        this.popoverElement.togglePopover()
      }
    }, 0)
  },

  // Manual positioning as a fallback for browsers that don't support anchor positioning. The anchor positioning
  // polyfill doesn't work for dynamically created elements (modals etc), so older browsers require JS-based styles.
  positionPopover(): void {
    if (supportsAnchorPositioning) return

    const toggleBox = this.popoverToggleElement.getBoundingClientRect()
    const toggleWidth = toggleBox.right - toggleBox.left
    this.popoverElement.style.width = `${toggleWidth}px`
    this.popoverElement.style.marginLeft = '-1px'

    // Popovers are outside regular document flow, so absolute positioning in relation to a parent element doesn't work
    this.popoverElement.style.top = `${toggleBox.bottom}px`

    // Show select options above if there's no space below
    const popoverHeight = this.popoverElement.clientHeight
    if (toggleBox.bottom + popoverHeight > window.scrollY + window.innerHeight) {
      this.popoverElement.style.top = `${toggleBox.top - popoverHeight - 3}px`
    }
  },

  // In 2026-04 Chrome supports setting styles for the popover based on whether a fallback is being used or not with
  // `@container anchored()`. However, border styles on the popover toggle need to be set with JS anyway as the selector
  // doesn't work for sibling elements, so for now it's easier to control all fallback-dependent styles with JS.
  updatePopoverStyles(): void {
    setTimeout(() => {
      this.positionPopover()
      this.popoverBelow =
        this.popoverToggleElement.getBoundingClientRect().bottom < this.popoverElement.getBoundingClientRect().bottom
    }, 0)
  },

  // Convert <option> tag string values to the correct type
  convertOptionValue(value: string): number | string {
    if (value === '') return value
    if (this.selectState.type === 'number' && isNaN(+value)) throw new Error('Not a numeric value')

    return this.selectState.type === 'number' ? +value : String(value)
  },

  // Split option labels to substrings with highlighting for search query matches (space-separated)
  optionLabelParts(label: string): { highlight: boolean; text: string }[] {
    if (!this.query.length) {
      return [{ highlight: false, text: label }]
    }

    const searchTerms = this.query.toLowerCase().split(/\s+/)

    return label
      .split(new RegExp(`(${searchTerms.map((str) => escapedString(str)).join('|')})`, 'gi'))
      .map((str) => ({ highlight: searchTerms.some((term) => term.toLowerCase() === str.toLowerCase()), text: str }))
      .filter(({ text }) => text.length)
  },

  OptionButton: {
    class:
      'w-full cursor-pointer px-3 py-1 text-left outline-none hover:bg-blue-400 hover:text-white \
      focus:bg-blue-500 focus:text-white disabled:text-gray-400 disabled:hover:bg-gray-400 disabled:hover:text-white',

    [':disabled'](this: OptionButtonElement): boolean {
      return this.option.disabled
    },
    ['@mousedown.prevent'](): void {},
    ['@click'](): void {
      const ctx = this as unknown as OptionButtonElement
      this.addSelection(ctx.option.value, ctx.$event)
    },
    ['@keydown.space.prevent'](): void {
      const ctx = this as unknown as OptionButtonElement
      this.addSelection(ctx.option.value, ctx.$event)
    },
    ['@keydown.enter.prevent'](): void {
      const ctx = this as unknown as OptionButtonElement
      this.addSelection(ctx.option.value, ctx.$event)
    },
    ['@keydown.down.prevent'](): void {
      const ctx = this as unknown as OptionButtonElement
      this.focusNextOption(ctx.$el)
    },
    ['@keydown.up.prevent'](): void {
      const ctx = this as unknown as OptionButtonElement
      this.focusPreviousOption(ctx.$el)
    },
  },
}))
